import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { Job, Variation } from '@cartwhl/common/types';
import { getStudioUrl } from '@cartwhl/common/utils';
import DaysAgo from '@cartwhl/ui/atoms/DaysAgo';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import Button from '@cartwhl/ui/input/Button';

import { galleryStore, noticesStore, userStore } from 'stores/global';

import GalleryList from './GalleryList';
import UserDetails from './UserDetails';

const FETCH_LIMIT = 1000;

type SortType = 'All' | 'Lists';
type SortBy = 'Newest' | 'Oldest';

export default function Favorites() {
  const { loading: userLoading, userInfo } = useSnapshot(userStore.state);
  const { favoritesRecords } = useSnapshot(galleryStore.state);

  const [sorted, setSorted] = useState<Job[]>([]);
  const [filter, setFilter] = useState<SortType>('All');
  const [sortBy, setSortBy] = useState<SortBy>('Newest');

  const records = useMemo(
    () => (favoritesRecords.data?.items || []) as Job[],
    [favoritesRecords.data?.items],
  );

  useEffect(() => {
    if (records.length > 0) {
      const list = Array.prototype.slice.call(records, 0);
      if (sortBy === 'Newest') {
        setSorted(list);
      } else {
        setSorted(list.reverse());
      }
    }
  }, [records, sortBy]);

  useEffect(() => {
    if (records.length === 0 && favoritesRecords.status === 'initial') {
      galleryStore.fetchFavorites(FETCH_LIMIT, false);
    }
  }, [records.length, favoritesRecords.status]);

  if (userLoading) {
    return (
      <div className="overview max-w-(--breakpoint-md) mx-auto">
        <div className="animate-pulse">
          <div className="h-24 w-24 rounded-full bg-gray-200" />
          <div className="mt-4 h-6 w-48 bg-gray-200" />
          <div className="mt-2 h-4 w-32 bg-gray-200" />
        </div>
        <div className="mt-8">
          <div className="h-6 w-32 bg-gray-200" />
          <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-video rounded-lg bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <UserDetails userInfo={userInfo} />
      <div className="mt-8">
        <div>
          <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
            Favorites
          </Text>
        </div>
        <div className="flex-rows mb-4 flex flex-nowrap gap-2">
          <Button
            variant={filter === 'All' ? 'secondary' : 'tertiary'}
            onClick={() => setFilter('All')}
          >
            All
          </Button>
          <Button
            variant={filter === 'Lists' ? 'secondary' : 'tertiary'}
            onClick={() => setFilter('Lists')}
          >
            Lists
          </Button>
          <Dropdown
            trigger={
              <Button
                variant="tertiary"
                icon={<SortIcon />}
                className="ml-auto"
              />
            }
          >
            <Text
              as="div"
              variant="web/body/md"
              className="px-4 py-2 opacity-30 dark:opacity-50"
            >
              Sort by
            </Text>
            <Dropdown.Item onClick={() => setSortBy('Newest')}>
              <div>
                <CheckmarkIcon
                  className={`mr-2 inline ${
                    sortBy === 'Newest' ? 'opacity-100' : 'opacity-0'
                  }`}
                />{' '}
                Newest
              </div>
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setSortBy('Oldest')}>
              <div>
                <CheckmarkIcon
                  className={`mr-2 inline ${
                    sortBy === 'Oldest' ? 'opacity-100' : 'opacity-0'
                  }`}
                />{' '}
                Oldest
              </div>
            </Dropdown.Item>
          </Dropdown>
        </div>
        {filter === 'Lists' ? (
          <div className="border-gray-30 dark:border-gray-60 relative rounded-lg border p-6">
            <Text
              as="p"
              variant="web/body/md"
              className="text-gray-45 dark:text-gray-60 text-center"
            >
              Coming soon
            </Text>
          </div>
        ) : (
          <FavoritesList sorted={sorted} />
        )}
        <div className="container my-10 flex justify-center">
          <InfiniteLoadingTrigger
            loading={favoritesRecords.status === 'pending'}
            onEnter={() => galleryStore.fetchFavorites(FETCH_LIMIT, false)}
            done={
              (favoritesRecords.status === 'ready' &&
                favoritesRecords.data?.nextToken === undefined) ||
              favoritesRecords.status === 'error'
            }
          />
        </div>
      </div>
    </div>
  );
}

function FavoritesList({ sorted }: { sorted: Job[] }) {
  const { favoritesRecords } = useSnapshot(galleryStore.state);

  const done =
    (favoritesRecords.status === 'ready' &&
      favoritesRecords.data?.nextToken === undefined) ||
    favoritesRecords.status === 'error';

  if (done && !galleryStore.hasFavorites()) {
    return (
      <div className="border-gray-30 dark:border-gray-60 relative rounded-lg border p-6">
        <Text
          as="p"
          variant="web/body/md"
          className="text-gray-45 dark:text-gray-60 text-center"
        >
          You have no favorites
        </Text>
      </div>
    );
  }

  // Create a custom asyncState with sorted items, preserving the exact type structure
  const sortedAsyncState =
    favoritesRecords.status === 'ready'
      ? {
          status: 'ready' as const,
          data: {
            items: sorted,
            nextToken: favoritesRecords.data?.nextToken,
          },
        }
      : favoritesRecords.status === 'pending'
        ? {
            status: 'pending' as const,
            data: favoritesRecords.data
              ? {
                  items:
                    sorted.length > 0 ? sorted : favoritesRecords.data.items,
                  nextToken: favoritesRecords.data.nextToken,
                }
              : undefined,
          }
        : favoritesRecords;

  return (
    <GalleryList
      asyncState={sortedAsyncState}
      variant="grid"
      maxPlaceholders={FETCH_LIMIT}
      onCardRender={(job: Job, variation: Variation) => (
        <GalleryFoot job={job} variation={variation} />
      )}
    />
  );
}

function GalleryFoot({ job, variation }: { job: Job; variation: Variation }) {
  const timeout = useRef<null | NodeJS.Timeout>(null);
  const [isActive, setIsActive] = useState<boolean>(false);
  const styles: { boxShadow?: string; display?: string } = {
    boxShadow: '-1rem 0 1rem white',
  };

  if (isActive) {
    styles.display = 'block';
  }

  return (
    <div>
      <p>
        <Text variant="web/body/md" className="opacity-50 dark:opacity-70">
          <DaysAgo date={job.createdAt} />
        </Text>
      </p>
      <div
        className="dark:bg-gray-90 z-1000 absolute right-0 top-0 hidden bg-white group-hover:block"
        style={styles}
      >
        <Dropdown
          trigger={<Button variant="tertiary" icon={<ThreeDotsIcon />} />}
          onOpenChange={(open) => {
            if (timeout.current) {
              clearTimeout(timeout.current);
            }
            if (open) {
              setIsActive(true);
            } else {
              timeout.current = setTimeout(setIsActive, 300, false);
            }
          }}
        >
          <Dropdown.Item
            onClick={() => {
              window.location.href = `${getStudioUrl()}/home?jobID=${
                job.jobID
              }&animationNumber=${variation.variationID}`;
            }}
          >
            Open
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              navigator.clipboard.writeText(
                `${getStudioUrl()}/home?jobID=${job.jobID}&animationNumber=${
                  variation.variationID
                }`,
              );
              noticesStore.info('URL Copied to clipboard!');
            }}
          >
            Copy share link
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              galleryStore.setFavorite(job.jobID, variation.variationID, false);
            }}
          >
            <span className="text-red">Remove favorite</span>
          </Dropdown.Item>
        </Dropdown>
      </div>
    </div>
  );
}
